import { capitalize } from 'lodash';
import { SportsEnum, VenueAccessTypesEnum } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { Props } from './types';

const US_SLUG = 'us';
export const getTitleLocationName = (city: Props['city']) => {
  if (city?.countrySubdivision?.country?.slug === US_SLUG && city?.countrySubdivision?.name) {
    return `${city?.name}, ${city?.countrySubdivision?.name}`;
  } else {
    return city?.name;
  }
};

type TextSectionProps = Props & { cityNameFull: string; cityNameShort: string };

export const generateFaqs = ({
  cityNameFull,
  cityNameShort,
  tournaments,
  city,
  stateTournaments,
  coaches,
  coachStarterProfiles,
  groups,
  sport,
}: TextSectionProps): { question: string; answer: string }[] => {
  const sportName = sport ? capitalize(sport) : 'Pickleball';
  const sportNameLower = sportName.toLowerCase();
  const courtsCount = city?.venues?.length || 0;
  const tournamentCount = tournaments?.length || 0;
  const stateTournamentCount = stateTournaments?.length || 0;
  const coachCount = coaches?.length || 0 + coachStarterProfiles?.length || 0;
  const groupCount = groups?.length || 0;
  const freeCourtCount =
    city?.venues?.filter((venue) => venue?.accessType === VenueAccessTypesEnum.Free)?.length || 0;

  const baseFaqs = [
    {
      question: `Can I get ${sportNameLower} lessons in ${cityNameFull}?`,
      answer: `${cityNameShort} has ${pluralize({
        count: coachCount,
        singular: `${sportNameLower} coach`,
        plural: `${sportNameLower} coaches`,
      })}. ${
        coachCount
          ? 'You can book with them directly on Bounce.'
          : 'No worries! You can take remote lessons with coaches on Bounce no matter where you live. Just upload a video and get feedback.'
      }`,
    },
    {
      question: `How many places are there to play ${sportNameLower} in ${cityNameFull}?`,
      answer: `There ${pluralize({
        count: courtsCount,
        singular: 'is',
        plural: 'are',
        hideCount: true,
      })} ${pluralize({
        count: courtsCount,
        singular: `${sportNameLower} court`,
        plural: `${sportNameLower} courts`,
      })} in ${cityNameShort} that are available to play ${sportNameLower} at. Recently, some the top places to play ${sportNameLower} have been: ${city?.venues
        ?.slice(0, 5)
        .map((v) => v.title)
        .join(', ')}.`,
    },
    {
      question: `Does ${cityNameFull} have free or public ${sportNameLower} courts?`,
      answer: `There ${pluralize({
        count: freeCourtCount,
        singular: 'is',
        plural: 'are',
        hideCount: true,
      })} ${pluralize({
        count: freeCourtCount,
        singular: 'free court',
        plural: 'free courts',
      })} in ${cityNameShort}. These courts do not require a membership or fee to play. You may still need to reserve a court or check the schedule of play.`,
    },
  ];

  if (!sport || sportNameLower !== SportsEnum.Pickleball.toLowerCase()) {
    return baseFaqs;
  } else {
    return [
      ...baseFaqs,
      {
        question: `How can I find people to play ${sportNameLower} with in ${cityNameFull}?`,
        answer: `There ${pluralize({
          count: groupCount,
          singular: 'is',
          plural: 'are',
          hideCount: true,
        })} ${pluralize({
          count: groupCount,
          singular: `${sportNameLower} group`,
          plural: `${sportNameLower} groups`,
        })} ${sportNameLower} groups in ${cityNameShort}. You can join groups to participate in their open plays and round robins`,
      },
      {
        question: `Can I compete in ${sportNameLower} tournaments in ${cityNameFull}?`,
        answer: `There ${pluralize({
          count: tournamentCount,
          singular: 'is',
          plural: 'are',
          hideCount: true,
        })} ${pluralize({
          count: tournamentCount,
          singular: 'upcoming tournament',
          plural: 'upcoming tournaments',
        })} in ${cityNameShort}. You can compete in ${pluralize({
          count: stateTournamentCount,
          singular: 'tournament',
          plural: 'tournaments',
        })} in ${city?.countrySubdivision?.name || 'your state'}.`,
      },
    ];
  }
};
